import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ActiveSessionsService } from '../services/active-sessions.service';
import { A11yModule } from '@angular/cdk/a11y';
import { ActiveSessionsModalComponent } from '../components/active-sessions-modal/active-sessions-modal.component';
import { ResponseModalComponent } from '../components/response-modal/response-modal.component';
import { ConfirmationModalComponent } from '../components/confirmation-modal/confirmation-modal.component';
import { filter, Observable, switchMap, tap } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { ActiveSession } from '@app/utils/interfaces/active-session.interface';
import { ActiveSessionsDisplayedColumns } from '@app/utils/enums/active-sessions-displayed-columns.enum';
import {
  RESPONSE_MODAL_CONFIGS,
  ACTIVE_SESSIONS_MODAL_CONFIGS,
  CONFIRMATION_MODAL_CONFIGS,
} from '@app/utils/constants/modal.constants';
import { ConfirmationResult } from '@app/utils/enums/confirmation-result.enum';

@Component({
  selector: 'app-active-sessions',
  standalone: true,
  imports: [TranslateModule, CommonModule, MatTableModule, IconComponent, A11yModule],
  templateUrl: './active-sessions.component.html',
  styleUrl: './active-sessions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveSessionsComponent implements OnInit {
  protected readonly IconName = IconName;
  protected readonly sessions = new MatTableDataSource<ActiveSession>();
  protected readonly displayedColumns: string[] = Object.values(ActiveSessionsDisplayedColumns);
  private readonly activeSessionsService = inject(ActiveSessionsService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly infoSummaryKeys = ['device', 'browser', 'location'];

  private isSortedByDescendingTime = true;

  public ngOnInit(): void {
    this.updateSessions().subscribe({
      error: () => {
        this.dialog.open(ResponseModalComponent, {
          data: false,
          ...RESPONSE_MODAL_CONFIGS,
        });
      },
    });
  }

  public showSessionInfo(session: ActiveSession): void {
    this.dialog.open(ActiveSessionsModalComponent, {
      data: session,
      ...ACTIVE_SESSIONS_MODAL_CONFIGS,
    });
  }

  public terminate(id: string, event: MouseEvent): void {
    event.stopPropagation();
    this.startTermination(id);
  }

  public terminateAllSessions(): void {
    this.startTermination();
  }

  private updateSessions(): Observable<ActiveSession[]> {
    return this.activeSessionsService.getSessions().pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((activeSessions: ActiveSession[]) => {
        this.sessions.data = activeSessions;
        this.sortSessions();
      }),
    );
  }

  public sortSessions(): void {
    this.sessions.data = this.sessions.data.sort(
      (session1: ActiveSession, session2: ActiveSession) => {
        const date1 = new Date(session1.lastActivityTime).getTime();
        const date2 = new Date(session2.lastActivityTime).getTime();

        return this.isSortedByDescendingTime ? date2 - date1 : date1 - date2;
      },
    );

    this.isSortedByDescendingTime = !this.isSortedByDescendingTime;
  }

  private startTermination(id?: string | undefined): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, CONFIRMATION_MODAL_CONFIGS);

    const modalConfig = {
      data: {
        success: false,
        type: id ? 'terminateOne' : 'terminateAll',
      },
      ...RESPONSE_MODAL_CONFIGS,
    };

    dialogRef
      .afterClosed()
      .pipe(
        filter((isTerminate: ConfirmationResult) => isTerminate === ConfirmationResult.Confirm),
        switchMap(() =>
          id
            ? this.activeSessionsService.terminateSession(id)
            : this.activeSessionsService.terminateAllSessions(),
        ),
        tap((response: HttpResponse<void>) => {
          modalConfig.data.success = response.ok;
          this.dialog.open(ResponseModalComponent, modalConfig);
        }),
        switchMap(() => {
          return this.updateSessions();
        }),
      )
      .subscribe({
        error: () => {
          this.dialog.open(ResponseModalComponent, modalConfig);
        },
      });
  }
}
